// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Deck editor runner.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { Apex, Deck } from "../../../package/Deck.js"
import { EDGE_TYPES } from "../../../package/index.js"

// @@imports-package
import { App } from "../app/App.js"
import {
    SteppedContainer,
    DeckToolbar,
    DeckRenderer,
    EdgeLengthEditor,
    EdgeTypeEditor
} from "../components/index.js"
import { cloneTemplate, crossFadeElements, fadeElementIn, renderLucideIcons } from "../utils/index.js"

// @@imports-module
import { Layout } from "./Layout.js"

// @@body
const eavesEditor = new Layout(async () => {
    const { container, state } = App.instance

    state.deck.client ??= new Deck({
        vertices: state.deck.default.deck.copy().vertices.array,
        isClosed: true
    })
    for (const { value: edge } of state.deck.client.edges) {
        state.deck.client.setEdgeType(edge, EDGE_TYPES.none)
    }
    if (state.deck.client.apexes.size === 0) {
        const apex = new Apex(
            Infinity,
            state.deck.client.boundingBox.width / 2,
            state.deck.client.boundingBox.width / 3.5,
            { isDefined: false }
        )
        state.deck.client.addApex(apex)
    }

    const toolbar = new DeckToolbar()
    const renderer = new DeckRenderer()

    state.deck.renderer = renderer
    const { dimensions } = state.deck.client.isDefined

    const stepProgressContent = /** @type {HTMLDivElement} */ (App
        .instance.container.progressContainer
        .querySelector("div.step-progress-content"))
    const deckEditorProgressContent = cloneTemplate("deck-editor-progress-content")

    const openToolbar = /** @type {HTMLButtonElement} */
        (deckEditorProgressContent.querySelector("button#open-toolbar"))
    const closeToolbar = /** @type {HTMLButtonElement} */
        (deckEditorProgressContent.querySelector("button#close-toolbar"))
    openToolbar.addEventListener("click", () => {
        toolbar.open()
        crossFadeElements(openToolbar, closeToolbar)
    })
    closeToolbar.addEventListener("click", () => {
        toolbar.close()
        crossFadeElements(closeToolbar, openToolbar)
    })

    stepProgressContent.append(deckEditorProgressContent)
    renderLucideIcons()
    fadeElementIn(stepProgressContent)

    const content = SteppedContainer.contentContainer()
    content.style.position = "relative" // move this to class and put change in stylesheet 
    content.style.overflow = "hidden"
    content.append(toolbar, renderer)

    await renderer.init()
    await container.load(content)

    if (!dimensions) {
        const popoverContainer = SteppedContainer.popoverContainer()
        const editor = new EdgeLengthEditor()

        popoverContainer.append(editor)
        container.loadPopover(popoverContainer)
    }
}, {
    id: "eaves-editor",
    title: "Enter Eaves Dimensions",
    name: "Eaves"
})

// @@exports
export { eavesEditor }
